import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { LoginService } from '../login/login.service';
import { Router } from '@angular/router';
import { environment } from 'projects/shared/src/environments/environment';
import { ApiService } from '../api-service/api.service';
import { applyForKsfInsurancePayload } from 'projects/shared/src/public-api';

@Injectable({
  providedIn: 'root',
})
export class KsfService {
  constructor(
    private router: Router,
    private apiService: ApiService,
    private loginService: LoginService,
    private http: HttpClient,
  ) {}

  /**
   *
   * @param data // Data from the KSF form
   * @param dealerId // Dealer ID from the dropdown.
   * @param leadOrigin
   */
  confirmInsurance(data: any, dealerId: number, leadOrigin: number = 2): Observable<any> {
    const payload = {
      ...data,
      dealer_id: dealerId,
      leadOrigin: leadOrigin,
    };
    const headers = new HttpHeaders({
      'Content-Type': 'application/json'
    });
    return this.http.post(environment.apiApplyForKsf, payload, { headers });
  }

  /**
   * Fetches the KSF template from the server.
   * If authentication is required, it will include the user's access token in the request headers.
   * If the token is expired, it attempts to refresh it before making the request.
   *
   * @param requireAuth - Determines whether authentication is required for this API call (default is true).
   * @returns Observable containing the API response or an error.
   */
  getKsfTemplate(requireAuth: boolean = true): Observable<any> {
    // Set initial headers for the API call
    let headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });

    // If authentication is required
    if (requireAuth) {
      const accessToken = this.loginService.getActiveToken();
      const refreshToken = this.loginService.getRefreshToken();

      // Check if access or refresh token is missing
      if (!accessToken || !refreshToken) {
        console.error('Error with tokens in getKsfTemplate');
        this.loginService.clearTokens();
        this.router.navigate(['/login']);
        return of(null);
      }

      // Check if the access token has expired
      const isExpired = this.loginService.isTokenExpired(accessToken);

      if (isExpired) {
        // If the token is expired, attempt to refresh the token
        return this.loginService.refreshAccessToken(refreshToken).pipe(
          switchMap(() => {
            // After refreshing, get the new access token and set headers
            const refreshedAccessToken = this.loginService.getActiveToken();
            headers = headers.set(
              'Authorization',
              `Bearer ${refreshedAccessToken}`
            );

            // Proceed with the API call
            return this.apiService.makeApiCall<any>(
              environment.apiGetKsfTemplate,
              headers,
              'GET'
            );
          }),
          catchError((error) => {
            // Handle error in token refreshing
            console.error('Error refreshing token', error);
            this.loginService.clearTokens();
            this.router.navigate(['/login']);
            return throwError(() => error);
          })
        );
      }

      // If the token is valid, set the Authorization header
      headers = headers.set('Authorization', `Bearer ${accessToken}`);
    }

    // Make the API call, either with or without authentication
    return this.apiService.makeApiCall<any>(
      environment.apiGetKsfTemplate,
      headers,
      'GET'
    );
  }

  /**
   * Fetches the initial KSF price based on the provided information.
   * If the access token is expired, it attempts to refresh the token before making the API call.
   *
   * @param KsfInitialPriceInfo - The information required to fetch the initial KSF price.
   * @returns Observable containing the API response or an error.
   */
  getInitialKsfPrice(KsfInitialPriceInfo: any): Observable<any> {
    let headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });

    // Get access and refresh tokens
    const accessToken = this.loginService.getActiveToken();
    const refreshToken = this.loginService.getRefreshToken();

    // If no tokens are available, redirect to login
    if (!accessToken || !refreshToken) {
      console.error('Error with tokens in getInitialKsfPrice');
      this.loginService.clearTokens();
      this.router.navigate(['/login']);
      return of(null);
    }

    // Check if the access token has expired
    const isTokenExpired = this.loginService.isTokenExpired(accessToken);

    // If the access token is expired, refresh it
    if (isTokenExpired) {
      return this.loginService.refreshAccessToken(refreshToken).pipe(
        switchMap(() => {
          // Get the refreshed access token
          const refreshedAccessToken = this.loginService.getActiveToken();
          headers = headers.set(
            'Authorization',
            `Bearer ${refreshedAccessToken}`
          );

          // Make the API call with the refreshed token
          return this.apiService.makeApiCall<any>(
            environment.apiGetInitialKsfPrice,
            headers,
            'POST',
            KsfInitialPriceInfo
          );
        }),
        catchError((error) => {
          // Handle token refresh failure and redirect to login if needed
          console.error('Error refreshing token', error);
          this.loginService.clearTokens();
          this.router.navigate(['/login']);
          return throwError(() => error);
        })
      );
    }

    // If the access token is valid, make the API call directly
    headers = headers.set('Authorization', `Bearer ${accessToken}`);
    return this.apiService.makeApiCall<any>(
      environment.apiGetInitialKsfPrice,
      headers,
      'POST',
      KsfInitialPriceInfo
    );
  }

  /**
   * Updates the KSF template with the provided information.
   * If the access token is expired, it attempts to refresh the token before making the API call.
   *
   * @param KsfUpdatePriceInfo - The information required to update the KSF template.
   * @returns Observable containing the API response or an error.
   */
  updateKsfTemplate(KsfUpdatePriceInfo: any): Observable<any> {
    let headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });

    return this.apiService.makeApiCall<any>(
      environment.apiUpdateKsfTemplate,
      headers,
      'POST',
      KsfUpdatePriceInfo
    );
  }

  /**
   * Applies for KSF with the provided information.
   * If the access token is expired, it attempts to refresh the token before making the API call.
   *
   * @param info - The information required to apply for KSF.
   * @returns Observable containing the API response or an error.
   */
  applyForKsf(info: applyForKsfInsurancePayload): Observable<any> {
    let headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });

    return this.apiService.makeApiCall<any>(
      environment.apiApplyForKsf,
      headers,
      'POST',
      info
    );
  }
}
